// src/app.js — State machine + screen router
// ─────────────────────────────────────────────────────────────
import { HomeScreen }           from './screens/Home.js';
import { CaptureScreen }        from './screens/Capture.js';
import { AnalyzingScreen }      from './screens/Analyzing.js';
import { AdditionalInputScreen } from './screens/AdditionalInput.js';
import { ResultScreen }         from './screens/Result.js';
import { LinkedInDraftScreen }  from './screens/LinkedInDraft.js';
import { PdfPreviewScreen }     from './screens/PdfPreview.js';
import { getUserLang }          from './i18n/index.js';

// ─── INITIAL STATE ─────────────────────────────────────────────
const INITIAL_STATE = {
  screen:          'home',
  lang:            getUserLang(),       // 'es' | 'en'
  image:           null,               // { base64: string, dataUrl: string }
  diagnosis:       null,               // DiagnosisResult JSON from Claude
  additionalInput: '',                 // percussion / pH result for p03/p09
  error:           null,               // string | null
  loadMsgIndex:    0,                  // cycling message index during analysis
};

// ─── SCREEN MAP ─────────────────────────────────────────────────
const SCREENS = {
  home:             HomeScreen,
  capture:          CaptureScreen,
  analyzing:        AnalyzingScreen,
  additional_input: AdditionalInputScreen,
  result:           ResultScreen,
  linkedin:         LinkedInDraftScreen,
  pdf_preview:      PdfPreviewScreen,
};

// ─── APP CORE ───────────────────────────────────────────────────
let _container = null;
let _state     = { ...INITIAL_STATE };
let _loadTimer = null;

/**
 * navigate — change screen and optionally update state
 * @param {string} screen
 * @param {object} [patch] - additional state fields to merge
 */
export function navigate(screen, patch = {}) {
  _state = { ..._state, screen, ...patch };
  _render();
}

/**
 * setState — partial state update without screen change
 * @param {object} patch
 */
export function setState(patch) {
  _state = { ..._state, ...patch };
  _render();
}

/**
 * getState — read-only snapshot
 */
export function getState() {
  return { ..._state };
}

/**
 * startLoadingCycle — cycles loadMsgIndex every 1800ms
 * Used during analysis screen
 */
export function startLoadingCycle(total = 5) {
  stopLoadingCycle();
  _loadTimer = setInterval(() => {
    _state = { ..._state, loadMsgIndex: (_state.loadMsgIndex + 1) % total };
    // Only update the message element to avoid full re-render flicker
    const el = document.querySelector('[data-load-msg]');
    if (el) {
      import('./screens/Analyzing.js').then(m => {
        el.textContent = m.LOAD_MSGS[_state.loadMsgIndex];
      });
    }
  }, 1800);
}

export function stopLoadingCycle() {
  if (_loadTimer) { clearInterval(_loadTimer); _loadTimer = null; }
}

/**
 * resetSession — clear image + diagnosis, go to capture
 */
export function resetSession() {
  stopLoadingCycle();
  navigate('capture', {
    image: null,
    diagnosis: null,
    additionalInput: '',
    error: null,
    loadMsgIndex: 0,
  });
}

// ─── PRIVATE ────────────────────────────────────────────────────
function _render() {
  if (!_container) return;
  const Screen = SCREENS[_state.screen];
  if (!Screen) {
    console.error(`ConcrePro: screen "${_state.screen}" not found`);
    return;
  }
  Screen(_container, _state, { navigate, setState, resetSession, startLoadingCycle, stopLoadingCycle });
}

/**
 * initApp — called once from main.js
 * @param {HTMLElement} container
 */
export function initApp(container) {
  _container = container;
  _render();
}
